// Sync Engine Core
// Centralized sync processing with connectivity monitoring, queue processing, and throttle control

import { offlineStateService } from '../../services/offline-state.service.js';
import { mutationQueue } from '../../queue/mutation-queue.js';
import { syncQueueRepository } from '../../db/repositories.js';
import { multiTabCoordinator } from '../../services/multi-tab-coordination.service.js';
import { OfflineConfig } from '../../config/offline-config.js';
import { MutationStatus } from '../types/sync-types.js';
import { syncTransport } from '../transport/execute-mutation.js';
import { queryClient, QueryKeys } from '../../../config/query-client.js';
import { cacheDebugLog } from '../../utils/cache-debug.js';
import { softInvalidateList } from '../../utils/optimistic-list-cache.js';
import {
  reconcileListCachesAfterSync,
  refetchEntityListsAfterSync,
} from '../utils/post-sync-reconcile.js';
import { syncRecoveryManager } from '../utils/sync-recovery.js';
import { offlineImageUploadService } from '../../services/offline-image-upload.service.js';
import { optimizeImage } from '../../../utils/optimizeImage.js';

class SyncEngine {
  constructor() {
    this.initialized = false;
    this.isProcessing = false;
    this.isLeader = false;
    this._processingQueue = false;
    this.throttleTimer = null;
    this.processingInterval = null;
    this.watchdogInterval = null;
    this.subscribers = new Set();
    this.paused = true;
    this.stats = {
      totalProcessed: 0,
      totalFailed: 0,
      totalConflicts: 0,
      lastSyncTime: null,
      avgSyncDuration: 0,
      syncDurations: [],
    };
    // Map of tempId → realId for offline-created entities.
    // Populated when a CREATE succeeds so that later DELETE/UPDATE mutations
    // can find the real MongoDB _id even after the CREATE is removed from the queue.
    this._tempIdMap = new Map();

    // Tracks the last-known isOnline value so handleNetworkChange only reacts
    // to actual transitions, not to spurious notifyListeners calls triggered by
    // pendingOperations changes (incrementPendingOperations / decrementPendingOperations
    // both call notifyListeners, which fires every subscriber including this engine).
    this._wasOnline = null;
  }

  /**
   * Initialize sync engine
   */
  initialize() {
    if (this.initialized) {
      console.log('[SyncEngine] Already initialized, skipping');
      return;
    }
    this.initialized = true;

    console.log('[SyncEngine] Initializing');
    cacheDebugLog('sync-engine-init', { online: offlineStateService.isCurrentlyOnline() });

    // Subscribe to offline state changes
    offlineStateService.subscribe((state) => {
      cacheDebugLog('sync-engine-network', { isOnline: state.isOnline });
      this.handleNetworkChange(state);
    });

    // Subscribe to multi-tab events
    multiTabCoordinator.subscribe('PRIMARY_CLAIM', (data) => {
      this.handleLeadershipChange(data);
    });

    // Check initial leadership
    this.checkLeadership();

    // Start processing if online
    if (offlineStateService.isCurrentlyOnline()) {
      this.startProcessing();
    }

    void syncRecoveryManager.recoverInterruptedSync();

    // Reset session retry counts on init so failed mutations get another 3 tries
    // after a page refresh / app restart
    mutationQueue.resetSessionRetryCounts().catch(() => {});

    console.log('[SyncEngine] Initialized');
  }

  /**
   * Check if this tab should be the sync leader
   */
  checkLeadership() {
    // Simple heuristic: first tab is leader
    // In production, use more sophisticated election
    if (!this.isLeader) {
      this.isLeader = true;
      this.broadcastLeadership();
      console.log('[SyncEngine] Became leader');
    }
  }

  /**
   * Broadcast leadership claim
   */
  broadcastLeadership() {
    multiTabCoordinator.broadcast({
      type: 'PRIMARY_CLAIM',
      data: { isLeader: true },
    });
  }

  /**
   * Handle leadership change from other tabs
   */
  handleLeadershipChange(data) {
    if (data.isLeader) {
      this.isLeader = false;
      console.log('[SyncEngine] Demoted to follower');
      this.stopProcessing();
    }
  }

  /**
   * Handle network state changes
   */
  handleNetworkChange(state) {
    // Ignore spurious notifications where isOnline hasn't actually changed.
    // incrementPendingOperations / decrementPendingOperations both call
    // notifyListeners, which delivers a full state object to every subscriber.
    // Without this guard, each API refetch (triggered by cache invalidation
    // after a successful sync) re-fires handleNetworkChange, causing the sync
    // engine to restart mid-processing — the progress bar oscillates forward
    // and backward as forceSync keeps getting called in a loop.
    if (this._wasOnline === state.isOnline) {
      return;
    }
    this._wasOnline = state.isOnline;

    if (state.isOnline && this.isLeader) {
      console.log('[SyncEngine] Network online, starting sync');
      this.startProcessing();
      if (!this.paused) {
        void this.forceSync();
      }
    } else if (!state.isOnline) {
      console.log('[SyncEngine] Network offline, stopping sync');
      this.stopProcessing();
    }
  }

  /**
   * Start sync processing
   */
  startProcessing() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    console.log('[SyncEngine] Starting sync processing');

    // Process immediately
    this.processQueue();

    // Set up periodic processing
    this.processingInterval = setInterval(() => {
      this.processQueue();
    }, OfflineConfig.queue.retryDelayBase);

    // Start watchdog that resets stuck 'processing' mutations
    this.startWatchdog();
  }

  /**
   * Stop sync processing
   */
  stopProcessing() {
    this.isProcessing = false;
    
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }

    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }

    this.stopWatchdog();

    console.log('[SyncEngine] Stopped sync processing');
  }

  /**
   * Pause or unpause sync processing
   */
  setPaused(paused) {
    this.paused = paused;
    console.log('[SyncEngine] Paused:', paused);
    this.notifySubscribers({ paused });
    if (!paused && this.isLeader && offlineStateService.isCurrentlyOnline()) {
      void this.processQueue();
    }
  }

  /**
   * Process mutation queue with throttling
   */
  async processQueue(bypassMutex = false) {
    if (!this.isProcessing || !this.isLeader) {
      return;
    }

    if (!offlineStateService.isCurrentlyOnline()) {
      return;
    }

    if (this.paused) {
      return;
    }

    // Check throttle (skip for manual sync)
    if (!bypassMutex && this.throttleTimer) {
      return;
    }

    // Mutex: prevent concurrent processQueue calls from racing on getPending.
    // Manual sync (bypassMutex=true) skips this check so the "Sync now" button
    // always runs even when the auto-processing interval is mid-cycle.
    if (!bypassMutex && this._processingQueue) {
      return;
    }
    this._processingQueue = true;

    try {
      // Retry any failed mutations before processing pending ones
      const retriedCount = await mutationQueue.retryFailed();
      if (retriedCount > 0) {
        console.log('[SyncEngine] Reset', retriedCount, 'failed mutation(s) to pending for retry');
      }

      // Phase 1: Process pending image uploads first
      await this.processPendingImageUploads();

      const startTime = Date.now();
      const pending = await mutationQueue.getPending();

      if (pending.length === 0) {
        return;
      }

      console.log(`[SyncEngine] Found ${pending.length} pending mutation(s) in queue`);
      cacheDebugLog('sync-engine-process-batch', { count: pending.length });

      // Process mutations in batches
      const batchSize = 5;
      let processed = 0;

      for (let i = 0; i < pending.length; i += batchSize) {
        const batch = pending.slice(i, i + batchSize);
        
        for (const mutation of batch) {
          if (!offlineStateService.isCurrentlyOnline()) break;
          try {
            const didWork = await this.processMutation(mutation);
            if (didWork !== false) processed++;
          } catch (error) {
            console.error('[SyncEngine] Failed to process mutation:', mutation.mutationId, error.message);
          }
        }

        // Throttle between batches
        if (i + batchSize < pending.length) {
          await this.throttle(100);
        }
      }

      const duration = Date.now() - startTime;
      this.updateStats(duration);

      console.log(`[SyncEngine] Processed ${processed} mutations in ${duration}ms`);

      // Notify subscribers
      this.notifySubscribers({ processed, duration });
    } catch (error) {
      console.error('[SyncEngine] Queue processing error:', error);
    } finally {
      this.throttleTimer = null;
      this._processingQueue = false;
    }
  }

  /**
   * Upload all pending offline images to Cloudinary and update queued mutations
   * that reference them with the real Cloudinary URLs.
   */
  async processPendingImageUploads() {
    const pendingImages = await offlineImageUploadService.getPending();
    if (pendingImages.length === 0) return;

    console.log(`[SyncEngine] Found ${pendingImages.length} pending image upload(s)`);
    this.notifySubscribers({ type: 'image-upload-start', total: pendingImages.length });

    let completed = 0;
    const total = pendingImages.length;

    for (const imageItem of pendingImages) {
      try {
        this.notifySubscribers({
          type: 'image-upload-progress',
          completed,
          total,
          progress: Math.round((completed / total) * 100),
          currentFile: imageItem.metadata?.originalName || imageItem.id,
        });

        const file = new File(
          [imageItem.blob],
          imageItem.metadata?.originalName || 'image.webp',
          { type: imageItem.blob.type || 'image/webp' }
        );

        const optimizedFile = await optimizeImage(file, 0.5);

        const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
        const UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

        if (!CLOUD_NAME || !UPLOAD_PRESET) {
          console.error('[SyncEngine] Missing Cloudinary env vars, skipping image upload');
          continue;
        }

        const formData = new FormData();
        formData.append("file", optimizedFile);
        formData.append("upload_preset", UPLOAD_PRESET);

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
          { method: "POST", body: formData }
        );

        const responseData = await response.json();
        if (!response.ok) {
          throw new Error(responseData.error?.message || 'Cloudinary upload failed');
        }

        const imageUrl = responseData.secure_url;
        const offlineRef = `offline://pending-img/${imageItem.id}`;

        console.log(`[SyncEngine] Uploaded image ${imageItem.id} → ${imageUrl}`);

        // Update all pending mutations that reference this offline image URL
        await this.replaceImageRefInPendingMutations(offlineRef, imageUrl);

        // Remove from image upload queue
        await offlineImageUploadService.remove(imageItem.id);
        completed++;
      } catch (error) {
        console.error(`[SyncEngine] Failed to upload image ${imageItem.id}:`, error.message);
      }
    }

    this.notifySubscribers({
      type: 'image-upload-complete',
      completed,
      total,
      progress: 100,
    });

    if (completed > 0) {
      console.log(`[SyncEngine] Uploaded ${completed}/${total} pending image(s)`);
    }
  }

  /**
   * Replace all occurrences of an offline image reference in pending mutations
   * with the real Cloudinary URL.
   */
  async replaceImageRefInPendingMutations(offlineRef, realUrl) {
    try {
      const allKeys = await syncQueueRepository.getKeys();
      const mutationKeys = allKeys.filter(k => k.startsWith('mutation:'));
      let replaced = 0;

      for (const key of mutationKeys) {
        try {
          const entry = await syncQueueRepository.getById(key);
          if (!entry?.data) continue;
          if (entry.data.status !== 'pending') continue;

          const payloadStr = JSON.stringify(entry.data.payload || {});
          const urlStr = entry.data.url || '';
          const combined = payloadStr + '|' + urlStr;

          if (!combined.includes(offlineRef)) continue;

          const updated = {};
          if (entry.data.payload && typeof entry.data.payload === 'object') {
            const raw = JSON.stringify(entry.data.payload);
            if (raw.includes(offlineRef)) {
              updated.payload = JSON.parse(raw.replaceAll(offlineRef, realUrl));
            }
          }

          if (typeof entry.data.url === 'string' && entry.data.url.includes(offlineRef)) {
            updated.url = entry.data.url.replaceAll(offlineRef, realUrl);
          }

          if (Object.keys(updated).length > 0) {
            await syncQueueRepository.update(key, updated);
            replaced++;
          }
        } catch {
        }
      }

      if (replaced > 0) {
        console.log(`[SyncEngine] Replaced ${replaced} mutation reference(s): ${offlineRef} → ${realUrl}`);
      }
    } catch (err) {
      console.error('[SyncEngine] Failed to replace image refs in mutations:', err);
    }
  }

  /**
   * Process a single mutation
   */
  async processMutation(mutation) {
    // ⛔ Dependency gate: if this mutation depends on other mutations,
    // skip it until all dependencies have been successfully synced.
    // If a dependency entry is no longer in the queue (removed by markAsSynced),
    // treat it as satisfied; if it still exists and isn't SYNCED, block.
    if (mutation.dependencies?.length > 0) {
      let blocked = false;
      for (const depId of mutation.dependencies) {
        const depEntry = await syncQueueRepository.getById(depId);
        if (depEntry && depEntry.data?.status !== MutationStatus.SYNCED) {
          blocked = true;
          break;
        }
      }
      if (blocked) {
        console.log(
          `[SyncEngine] Skipping ${mutation.mutationId}: dependencies not yet synced (${mutation.dependencies.join(', ')})`
        );
        return false; // Stay PENDING — next cycle will retry
      }
    }

    try {
      await mutationQueue.updateStatus(mutation.mutationId, MutationStatus.PROCESSING);

      // Reload the latest mutation data from queue before executing, in case
      // a preceding CREATE mutation's rewritePendingMutations updated the
      // entityId/URL (e.g. from temp-* to real MongoDB _id).
      const latestEntry = await syncQueueRepository.getById(mutation.mutationId);
      let effectiveMutation = latestEntry?.data || mutation;

      // If the mutation still references a temp ID in its URL, look up the real
      // ID from the temp→real map (populated by a previously-synced CREATE).
      if (effectiveMutation.url &&
          typeof effectiveMutation.url === 'string' &&
          (effectiveMutation.url.includes('temp-') || effectiveMutation.url.includes('/cr-'))) {
        for (const [tempId, realId] of this._tempIdMap) {
          if (effectiveMutation.url.includes(tempId)) {
            effectiveMutation = {
              ...effectiveMutation,
              url: effectiveMutation.url.replaceAll(tempId, realId),
              entityId: realId,
            };
            console.log(`[SyncEngine] Resolved temp ID ${tempId} → ${realId} for mutation ${mutation.mutationId}`);
            break;
          }
        }
      }

      const serverResult = await this.executeMutation(effectiveMutation);

      // After a CREATE succeeds, rewrite any pending mutations (DELETE/UPDATE)
      // that still reference the temp clientRequestId in their URL/entityId.
      // This fixes the bug where create+delete of the same entity offline causes
      // the delete to fail because it references the clientRequestId instead of
      // the real MongoDB _id returned by the server.
      if (mutation.operationType === 'create' && serverResult) {
        // Store temp→real mapping for this entity so DELETE/UPDATE mutations
        // can find the real ID even after the CREATE is removed from the queue.
        const realEntityId = serverResult?._id ?? serverResult?.id ??
          serverResult?.dayBook?._id ?? serverResult?.daybook?._id ??
          serverResult?.trainer?._id ?? serverResult?.athlete?._id ??
          serverResult?.program?._id ?? serverResult?.diet?._id ??
          serverResult?.result?._id ?? serverResult?.data?._id;
        if (realEntityId && effectiveMutation.entityId && effectiveMutation.entityId !== realEntityId) {
          this._tempIdMap.set(effectiveMutation.entityId, realEntityId);
          // Also map payload clientRequestId if different
          if (effectiveMutation.payload?.clientRequestId &&
              effectiveMutation.payload.clientRequestId !== effectiveMutation.entityId) {
            this._tempIdMap.set(effectiveMutation.payload.clientRequestId, realEntityId);
          }
        }
        // Also map the _optimisticId (temp-xxx) from the payload, which is what
        // DELETE/UPDATE mutations reference in their URL/entityId.
        const optimisticId = effectiveMutation.payload?._optimisticId;
        if (optimisticId && optimisticId !== realEntityId) {
          this._tempIdMap.set(optimisticId, realEntityId);
        }
        await this.rewritePendingMutations(mutation, serverResult);
      }

      // Remove from queue immediately after a successful replay so a reload or
      // post-sync cache error cannot enqueue the same create twice on the server.
      await mutationQueue.markAsSynced(mutation.mutationId);
      this.stats.lastSyncTime = Date.now();
      this.stats.totalProcessed++;

      try {
        await this.handleSyncSuccess(mutation, serverResult);
      } catch (postSyncError) {
        console.error(
          '[SyncEngine] Post-sync bookkeeping failed (mutation already synced):',
          mutation.mutationId,
          postSyncError
        );
      }
    } catch (error) {
      if (error.message.includes('Conflict detected')) {
        console.log('[SyncEngine] Conflict detected for mutation:', mutation.mutationId);
      } else {
        // Distinguish transient network errors (timeout, DNS, etc.) from
        // permanent server errors. Transient errors during weak Internet
        // must NOT consume retry budgets or mark mutations as FAILED —
        // the mutation stays PENDING and the engine pauses once the
        // connectivity threshold is reached.
        const isTransient =
          error.message.includes('timeout') ||
          error.message.includes('network') ||
          error.message.includes('fetch') ||
          error.message.includes('Failed to fetch') ||
          error.name === 'TypeError';

        if (isTransient) {
          offlineStateService.markNetworkFailure();
          await mutationQueue.updateStatus(
            mutation.mutationId,
            MutationStatus.PENDING,
            mutation.retryCount || 0,
          );
          console.log(
            '[SyncEngine] Transient error — keeping mutation PENDING:',
            mutation.mutationId,
            error.message,
          );
        } else {
          this.stats.totalFailed++;
          // Increment both total retryCount and sessionRetryCount
          // sessionRetryCount resets to 0 on syncNow / page reload
          const retryCount = (mutation.retryCount || 0) + 1;
          const sessionRetryCount = (mutation.sessionRetryCount || 0) + 1;
          await mutationQueue.updateStatus(mutation.mutationId, MutationStatus.FAILED, retryCount);
          await syncQueueRepository.update(mutation.mutationId, { sessionRetryCount });
        }
      }
      throw error;
    }
  }

  /**
   * Execute mutation against server
   */
  async executeMutation(mutation) {
    const result = await syncTransport.executeMutation(mutation);
    
    if (result.status === 'success') {
      return result.result;
    } else if (result.status === 'conflict') {
      await mutationQueue.updateStatus(mutation.mutationId, MutationStatus.CONFLICT);
      this.stats.totalConflicts++;
      throw new Error(`Conflict detected: ${result.conflictType}`);
    } else if (result.status === 'error') {
      throw new Error(result.error);
    } else if (result.status === 'skipped') {
      return result.result;
    } else {
      throw new Error('Unknown sync result status');
    }
  }

  /**
   * After a CREATE mutation succeeds on the server, rewrite any pending mutations
   * (DELETE/UPDATE) that still reference the temp clientRequestId in their URL or
   * entityId. This fixes the bug where create+delete of the same entity while
   * offline causes the DELETE to fail because it uses the clientRequestId in the
   * URL path, but the server assigned a real MongoDB _id.
   */
  /**
   * Deep-replace all occurrences of any temp ID with the real MongoDB _id
   * inside a value (string, array, or plain object).  Used to fix up payload
   * fields such as athleteId, trainerId, etc. that reference a client-generated
   * temp ID.
   */
  _replaceTempIdsDeep(value, tempIds, realId) {
    if (typeof value === 'string') {
      return tempIds.includes(value) ? realId : value;
    }
    if (Array.isArray(value)) {
      return value.map((v) => this._replaceTempIdsDeep(v, tempIds, realId));
    }
    if (value && typeof value === 'object' && value.constructor === Object) {
      const next = {};
      for (const [k, v] of Object.entries(value)) {
        next[k] = this._replaceTempIdsDeep(v, tempIds, realId);
      }
      return next;
    }
    return value;
  }

  /**
   * After a CREATE mutation succeeds on the server, rewrite any pending mutations
   * (DELETE/UPDATE) that still reference the temp clientRequestId in their URL,
   * entityId, or **payload fields**.  This fixes the bug where create+delete of
   * the same entity offline causes the delete to fail because it references the
   * clientRequestId instead of the real MongoDB _id returned by the server.
   *
   * The entityType filter was intentionally removed so cross-entity mutations
   * (e.g. locker assignment referencing an athlete temp ID) are also rewritten.
   */
  async rewritePendingMutations(createMutation, serverResult) {
    // Server responses often wrap the created entity in a nested field
    // e.g. { dayBook: { _id: '...' } }, { trainer: { _id: '...' } }, { athlete: { _id: '...' } }
    let realId = serverResult?._id ?? serverResult?.id;
    if (!realId) {
      // Try common wrapper fields
      realId = serverResult?.dayBook?._id ?? serverResult?.daybook?._id ??
               serverResult?.trainer?._id ?? serverResult?.athlete?._id ??
               serverResult?.program?._id ?? serverResult?.diet?._id ??
               serverResult?.result?._id ?? serverResult?.data?._id;
    }
    if (!realId) return;

    const clientRequestId = createMutation.entityId;
    if (!clientRequestId || clientRequestId === realId) return;
    // Only rewrite if the entityId looks like a client-generated temp ID
    if (typeof clientRequestId !== 'string' || /^[a-fA-F0-9]{24}$/.test(clientRequestId)) return;

    // Also get the clientRequestId from the payload (cr-* format) in case
    // entityId is temp-* but payload has cr-*
    const payloadClientId = createMutation.payload?.clientRequestId;
    // Get the optimisticId (temp-xxx) from the payload — added by hooks like
    // useCreateDaybook.onMutate so DELETE/UPDATE mutations can be correlated.
    const optimisticId = createMutation.payload?._optimisticId;

    // Collect all temp IDs that should be replaced by this real ID
    const tempIds = [
      clientRequestId,
      ...(payloadClientId && payloadClientId !== clientRequestId ? [payloadClientId] : []),
      ...(optimisticId && optimisticId !== clientRequestId && optimisticId !== payloadClientId ? [optimisticId] : []),
    ];

    try {
      const allKeys = await syncQueueRepository.getKeys();
      const mutationKeys = allKeys.filter(k => k.startsWith('mutation:'));
      let rewritten = 0;

      for (const key of mutationKeys) {
        try {
          const entry = await syncQueueRepository.getById(key);
          if (!entry?.data) continue;
          if (entry.data.status !== 'pending') continue;

          const mutationId = entry.data.mutationId;
          if (mutationId === createMutation.mutationId) continue;

          // Build a combined string of entityId + url + stringified payload
          // to quickly check whether this mutation references any temp ID.
          const entryPayloadStr = entry.data.payload ? JSON.stringify(entry.data.payload) : '';
          const combined = `${entry.data.entityId || ''}|${entry.data.url || ''}|${entryPayloadStr}`;
          const matchesAnyTemp = tempIds.some((t) => t && combined.includes(t));
          if (!matchesAnyTemp) continue;

          const updated = {
            entityId: realId,
          };

          // Replace any temp ID reference in the URL path with the real _id
          let updatedUrl = entry.data.url;
          if (typeof updatedUrl === 'string') {
            for (const t of tempIds) {
              if (t && updatedUrl.includes(t)) {
                updatedUrl = updatedUrl.replaceAll(t, realId);
              }
            }
            if (updatedUrl !== entry.data.url) {
              updated.url = updatedUrl;
            }
          }

          // Deep-scan payload for temp IDs and replace them.
          // Skip if the payload string (already serialized above) doesn't contain
          // any temp ID — avoids unnecessary object allocation and IndexedDB writes.
          if (
            entry.data.payload &&
            typeof entry.data.payload === 'object' &&
            tempIds.some((t) => t && entryPayloadStr.includes(t))
          ) {
            const rewrittenPayload = this._replaceTempIdsDeep(entry.data.payload, tempIds, realId);
            if (rewrittenPayload !== entry.data.payload) {
              updated.payload = rewrittenPayload;
            }
          }

          await syncQueueRepository.update(key, updated);
          rewritten++;
          // Also store in tempIdMap for future retry cycles
          if (entry.data.entityId && entry.data.entityId !== realId) {
            this._tempIdMap.set(entry.data.entityId, realId);
          }
        } catch {
          // skip individual read/write errors
        }
      }

      if (rewritten > 0) {
        console.log(
          `[SyncEngine] Rewrote ${rewritten} pending mutation(s) from temp IDs to real _id ${realId}`
        );
      }

      // ── Fallback: scan ALL pending mutations for any temp-* / cr-* value ──
      // that might be referenced in the _tempIdMap.  This catches edge cases
      // where the create mutation's payload didn't carry an _optimisticId
      // (e.g. legacy hooks that haven't been updated).
      if (this._tempIdMap.size > 0) {
        let fallbackRewritten = 0;
        for (const key of mutationKeys) {
          try {
            const entry = await syncQueueRepository.getById(key);
            if (!entry?.data) continue;
            if (entry.data.status !== 'pending') continue;
            if (entry.data.mutationId === createMutation.mutationId) continue;

            const entryStr = JSON.stringify(entry.data);
            // Find any temp-* or cr-* value in the mutation that exists in _tempIdMap
            const found = [...this._tempIdMap.entries()]
              .find(([tempId]) => entryStr.includes(tempId));
            if (!found) continue;

            const [foundTempId, foundRealId] = found;
            const fbUpdated = {};
            if (entry.data.entityId && entry.data.entityId !== foundRealId) {
              fbUpdated.entityId = foundRealId;
            }
            if (typeof entry.data.url === 'string' && entry.data.url.includes(foundTempId)) {
              fbUpdated.url = entry.data.url.replaceAll(foundTempId, foundRealId);
            }
            if (entry.data.payload && typeof entry.data.payload === 'object') {
              const rewrittenPayload = this._replaceTempIdsDeep(
                entry.data.payload, [foundTempId], foundRealId
              );
              if (rewrittenPayload !== entry.data.payload) {
                fbUpdated.payload = rewrittenPayload;
              }
            }
            if (Object.keys(fbUpdated).length > 0) {
              await syncQueueRepository.update(key, fbUpdated);
              fallbackRewritten++;
            }
          } catch {
            // skip individual read/write errors
          }
        }
        if (fallbackRewritten > 0) {
          console.log(
            `[SyncEngine] Fallback rewrote ${fallbackRewritten} pending mutation(s) via _tempIdMap (real _id ${realId})`
          );
        }
      }
    } catch (err) {
      console.error('[SyncEngine] Failed to rewrite pending mutations:', err);
    }
  }

  /**
   * Handle successful sync
   */
  async handleSyncSuccess(mutation, serverResult) {
    cacheDebugLog('sync-success', {
      mutationId: mutation.mutationId,
      entityType: mutation.entityType,
      operationType: mutation.operationType,
      serverId: serverResult?._id ?? serverResult?.id,
    });

    multiTabCoordinator.notifyEntityUpdated(mutation.entityType, mutation.entityId);

    await reconcileListCachesAfterSync(
      mutation.entityType,
      mutation.operationType,
      serverResult
    );

    await this.invalidateClientCachesAfterSync(mutation, serverResult);

    // Refetch list data after create, update, or delete so the UI reflects
    // the authoritative server state — especially critical for delete-after-create
    // scenarios where the CREATE's refetch included the item, but the subsequent
    // DELETE doesn't clear the stale list data without an explicit refetch.
    if (mutation.operationType === 'create' || mutation.operationType === 'update' || mutation.operationType === 'delete') {
      await refetchEntityListsAfterSync(mutation.entityType);
    }

    this.notifySubscribers({ type: 'mutation-synced', mutation, serverResult });
  }

  /**
   * Clear offline IndexedDB slices and invalidate React Query so UI matches server after sync.
   */
  async invalidateClientCachesAfterSync(mutation, serverResult) {
    const { entityType, entityId, operationType } = mutation;

    const inv = (keys) => {
      for (const qk of keys) {
        softInvalidateList(queryClient, qk);
      }
    };

    const id = serverResult?._id ?? serverResult?.id ?? entityId;

    switch (entityType) {
      case 'athletes':
        inv([QueryKeys.ATHLETES, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD, QueryKeys.DAYBOOKS]);
        if (id) {
          softInvalidateList(queryClient, QueryKeys.ATHLETE(id));
        }
        break;
      case 'trainers':
        inv([QueryKeys.TRAINERS, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        if (id) {
          softInvalidateList(queryClient, QueryKeys.TRAINER(id));
        }
        break;
      case 'programs':
        inv([QueryKeys.PROGRAMS, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        if (id) {
          softInvalidateList(queryClient, QueryKeys.PROGRAM(id));
        }
        break;
      case 'diets':
        inv([QueryKeys.DIETS, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        if (id) {
          softInvalidateList(queryClient, QueryKeys.DIET(id));
        }
        break;
      case 'daybooks':
        inv([QueryKeys.DAYBOOKS, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        if (id) {
          softInvalidateList(queryClient, QueryKeys.DAYBOOK(id));
        }
        break;
      case 'lockers':
        inv([QueryKeys.LOCKERS, QueryKeys.LOCKER_CONFIG, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        break;
      case 'payments':
        inv([QueryKeys.PAYMENTS, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        break;
      case 'memberships':
        inv([QueryKeys.MEMBERSHIP, QueryKeys.ATHLETES, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        break;
      case 'gyms':
        inv([QueryKeys.GYM, QueryKeys.GYM_ME, QueryKeys.GYM_DASHBOARD, QueryKeys.DASHBOARD]);
        break;
      case 'exercises':
        inv([QueryKeys.EXERCISES, QueryKeys.PROGRAMS]);
        break;
      case 'meals':
        inv([QueryKeys.MEALS, QueryKeys.DIETS]);
        break;
      default:
        inv([QueryKeys.DASHBOARD, QueryKeys.GYM_DASHBOARD]);
    }

    cacheDebugLog('sync-rq-invalidated', { entityType, operationType, id });
  }

  // ── Watchdog ──────────────────────────────────────────────────────────────
  // Periodically resets mutations stuck in 'processing' back to 'pending'
  // so they get retried. A mutation is considered stuck if it has been in
  // 'processing' state for more than STUCK_THRESHOLD_MS without progressing.

  startWatchdog() {
    if (this.watchdogInterval) return;
    const CHECK_INTERVAL = 15_000;        // check every 15 s
    const STUCK_THRESHOLD_MS = 30_000;    // stuck after 30 s in processing

    this.watchdogInterval = setInterval(async () => {
      try {
        const stats = await mutationQueue.getStats();
        if (stats.processing === 0) return;

        // We have stuck mutations; reset them to pending so they are retried
        const allKeys = await mutationQueue.getPending().catch(() => []);
        // getPending only returns 'pending' — we need to query processing separately
        const queueKeys = await syncQueueRepository.getKeys().catch(() => []);
        const mutationKeys = queueKeys.filter(k => k.startsWith('mutation:'));

        let resetCount = 0;
        for (const key of mutationKeys) {
          try {
            const entry = await Promise.race([
              syncQueueRepository.getById(key),
              new Promise(resolve => setTimeout(() => resolve(null), 2000)),
            ]);
            if (!entry?.data) continue;

            if (entry.data.status === MutationStatus.PROCESSING) {
              const age = Date.now() - (entry.data.lastAttemptAt || entry.data.createdAt || 0);
              if (age > STUCK_THRESHOLD_MS) {
                await mutationQueue.updateStatus(key, MutationStatus.PENDING, entry.data.retryCount || 0);
                resetCount++;
              }
            }
          } catch {
            // skip individual read errors
          }
        }

        if (resetCount > 0) {
          console.log(`[SyncEngine] Watchdog reset ${resetCount} stuck mutation(s) to pending`);
          this.notifySubscribers({ type: 'watchdog-reset', count: resetCount });
          // Kick processing so stuck mutations are retried immediately
          void this.processQueue();
        }
      } catch (err) {
        console.warn('[SyncEngine] Watchdog error:', err);
      }
    }, CHECK_INTERVAL);
  }

  stopWatchdog() {
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
  }

  /**
   * Throttle processing
   */
  throttle(delay) {
    return new Promise(resolve => {
      this.throttleTimer = setTimeout(resolve, delay);
    });
  }

  /**
   * Update sync statistics
   */
  updateStats(duration) {
    this.stats.syncDurations.push(duration);
    
    // Keep only last 100 durations
    if (this.stats.syncDurations.length > 100) {
      this.stats.syncDurations.shift();
    }

    // Calculate average
    const sum = this.stats.syncDurations.reduce((a, b) => a + b, 0);
    this.stats.avgSyncDuration = Math.floor(sum / this.stats.syncDurations.length);
  }

  /**
   * Subscribe to sync events
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify subscribers
   */
  notifySubscribers(event) {
    this.subscribers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[SyncEngine] Subscriber error:', error);
      }
    });
  }

  /**
   * Get sync statistics
   */
  getStats() {
    return {
      ...this.stats,
      isProcessing: this.isProcessing,
      isLeader: this.isLeader,
    };
  }

  /**
   * Pause sync processing
   */
  pause() {
    this.stopProcessing();
    console.log('[SyncEngine] Paused');
  }

  /**
   * Resume sync processing
   */
  resume() {
    if (this.isLeader && offlineStateService.isCurrentlyOnline()) {
      this.startProcessing();
    }
    console.log('[SyncEngine] Resumed');
  }

  /**
   * Force immediate sync
   */
  async forceSync() {
    if (!this.isLeader) {
      return;
    }

    console.log('[SyncEngine] Forcing immediate sync');
    await this.processQueue();
  }

  /**
   * Force sync even when paused — used by the explicit "Sync now" button in Settings.
   * Resets session retry counts so FAILED mutations get another 3 tries.
   * Also ensures the engine is initialized and processing is started so the
   * sync actually runs, even if the engine was previously idle or not the leader.
   */
  async syncNow() {
    // Ensure the engine is initialized
    if (!this.initialized) {
      this.initialize();
    }

    // Always claim leadership for the sync-now action
    this.isLeader = true;

    // Ensure processing is active
    if (!this.isProcessing) {
      this.startProcessing();
    }

    const wasPaused = this.paused;
    this.paused = false;
    this.notifySubscribers({ type: 'SYNC_NOW_TRIGGERED' });
    await mutationQueue.resetSessionRetryCounts();
    // Bypass the _processingQueue mutex so manual sync always runs,
    // even when the auto-processing interval is mid-cycle
    await this.processQueue(true);

    // Only restore paused state if it was paused by the system (not user action)
    // This keeps the engine processing after a manual sync until it's naturally stopped
    if (wasPaused) {
      this.paused = true;
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    this.stopProcessing();
    this.subscribers.clear();
    console.log('[SyncEngine] Destroyed');
  }
}

// Export singleton instance
export const syncEngine = new SyncEngine();
